import * as React from 'react';
import React__default, { useRef, useEffect, useState } from 'react';
import { FaMemory } from '@react-icons/all-files/fa/FaMemory';
import { RiCpuLine } from '@react-icons/all-files/ri/RiCpuLine';
import { RiCpuFill } from '@react-icons/all-files/ri/RiCpuFill';
import { VscPulse } from '@react-icons/all-files/vsc/VscPulse';
import { AiOutlineCodeSandbox } from '@react-icons/all-files/ai/AiOutlineCodeSandbox';
import { FaRegImages } from '@react-icons/all-files/fa/FaRegImages';
import { FiLayers } from '@react-icons/all-files/fi/FiLayers';
import { BsTriangle } from '@react-icons/all-files/bs/BsTriangle';
import { VscActivateBreakpoints } from '@react-icons/all-files/vsc/VscActivateBreakpoints';
import { FaServer } from '@react-icons/all-files/fa/FaServer';
import { RiArrowDownSFill } from '@react-icons/all-files/ri/RiArrowDownSFill';
import { RiArrowRightSFill } from '@react-icons/all-files/ri/RiArrowRightSFill';
import { GiPauseButton } from '@react-icons/all-files/gi/GiPauseButton';
import * as ReactDOM from 'react-dom';
import { useThree, addEffect, addAfterEffect, addTail } from '@react-three/fiber';
import create from 'zustand';
import { styled } from '@stitches/react';

function _extends(props, args) {
  return Object.assign(props, args);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

const Html = React.forwardRef((_ref, ref) => {
  var _portal$current;

  let {
    portal,
    className,
    children
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, ['portal', 'children', 'className']);

  const {
    gl
  } = useThree(({
    gl
  }) => ({
    gl
  }));
  const [el] = React.useState(() => document.createElement('div'));
  const group = React.useRef(null);
  const target = (_portal$current = portal == null ? void 0 : portal.current) != null ? _portal$current : gl.domElement.parentNode;
  React.useEffect(() => {
    if (group.current) {
      if (target) {
        target.appendChild(el);
      }

      return () => {
        if (target) target.removeChild(el);
        ReactDOM.unmountComponentAtNode(el);
      };
    }

    return undefined;
  }, [target, el]);
  React.useLayoutEffect(() => {
    ReactDOM.render(React.createElement('div', {
      ref: ref,
      className: className,
      children: children
    }), el);
  });
  return React.createElement('group', _extends(props, {
    ref: group
  }));
});

class GLPerf {
  constructor(settings = {}) {
    this.names = [''];
    this.finished = [];
    this.gl = void 0;
    this.extension = void 0;
    this.paused = false;
    this.isWebGL2 = true;
    this.cpuAccums = [];
    this.gpuAccums = [];
    this.activeAccums = [];
    this.chart = [];
    this.gpuChart = [];
    this.cpuChart = [];

    this.paramLogger = () => {};

    this.glFinish = () => {};

    this.addProfiler = () => {};

    this.chartLogger = () => {};

    this.chartLen = 60;
    this.chartHz = 10;
    this.factorGPU = 1;
    this.trackGPU = true;
    this.chartFrame = 0;
    this.gpuTimeProcess = 0;
    this.chartTime = 0;
    this.activeQueries = 0;
    this.circularId = 0;
    this.detected = 0;
    this.frameId = 0;
    this.paramFrame = 0;
    this.paramTime = 0;

    this.now = () => {};

    this.t0 = 0;
    window.GLPerf = window.GLPerf || {};
    Object.assign(this, settings);
    this.chart = new Array(this.chartLen).fill(0);
    this.gpuChart = new Array(this.chartLen).fill(0);
    this.cpuChart = new Array(this.chartLen).fill(0);

    this.now = () => window.performance && window.performance.now ? window.performance.now() : Date.now();

    this.initGpu();
    this.is120hz();
  }

  initGpu() {
    if (this.gl) {
      this.isWebGL2 = true;
      this.extension = this.gl.getExtension('EXT_disjoint_timer_query_webgl2');

      if (this.extension === null) {
        this.isWebGL2 = false;
        this.extension = this.gl.getExtension('EXT_disjoint_timer_query');

        if (this.extension === null) {
          console.warn('Disjoint_time_query extension not available.');
          this.isGL();
        }
      } else {
        this.isGL();
      }
    }
  }

  isGL() {
    const gl = this.gl;

    if (gl) {
      const resolved = (t, activeAccums) => {
        setTimeout(() => {
          gl.getError();
          const dt = this.now() - t;
          activeAccums.forEach((active, i) => {
            if (active) {
              this.gpuAccums[i] += dt;
            }
          });
        });
      };

      const glFinish = async function glFinish(t, activeAccums) {
        return await Promise.resolve(resolved(t, activeAccums));
      };

      const addProfiler = (fn, self, target) => function () {
        const t = self.now();
        fn.apply(target, arguments);
        self.finished.push(glFinish(t, self.activeAccums.slice(0)));
      };

      ['drawArrays', 'drawElements', 'drawArraysInstanced', 'drawBuffers', 'drawElementsInstanced', 'drawRangeElements'].forEach(fn => {
        if (gl[fn]) {
          gl[fn] = addProfiler(gl[fn], this, gl);
        }
      });

      gl.getExtension = ((fn, self) => function () {
        let ext = fn.apply(gl, arguments);

        if (ext) {
          ['drawElementsInstancedANGLE', 'drawBuffersWEBGL'].forEach(fn => {
            if (ext[fn]) ext[fn] = addProfiler(ext[fn], self, ext);
          });
        }

        return ext;
      })(gl.getExtension, this);
    }
  }

  is120hz() {
    let rafId;
    let n = 0;

    const loop = t => {
      if (++n < 20) {
        rafId = window.requestAnimationFrame(loop);
      } else {
        this.detected = Math.ceil(1e3 * n / (t - this.t0) / 70);
        window.cancelAnimationFrame(rafId);
      }

      if (!this.t0) this.t0 = t;
    };

    window.requestAnimationFrame(loop);
  }

  addUI(name) {
    if (this.names.indexOf(name) === -1) {
      this.names.push(name);
      this.cpuAccums.push(0);
      this.gpuAccums.push(0);
      this.activeAccums.push(false);
    }
  }

  nextFrame(now) {
    this.frameId++;
    const t = now || this.now();
    const duration = t - this.paramTime;
    let cpu = 0;
    let gpu = 0;

    if (this.frameId <= 1) {
      this.paramFrame = this.frameId;
      this.paramTime = t;
    } else {
      if (duration >= 1e3) {
        const frameCount = this.frameId - this.paramFrame;
        const fps = frameCount / duration * 1e3;

        for (let i = 0; i < this.names.length; i++) {
          cpu = this.cpuAccums[i] / duration;
          gpu = this.isWebGL2 ? this.gpuAccums[i] : this.gpuAccums[i] / duration;
          const mem = Math.round(window.performance && window.performance.memory ? window.performance.memory.usedJSHeapSize / (1 << 20) : 0);
          this.paramLogger({
            i,
            cpu,
            gpu,
            mem,
            fps: Math.round(fps * 10) / 10,
            duration: Math.round(duration),
            frameCount
          });
          this.cpuAccums[i] = 0;

          if (this.isWebGL2) {
            this.gpuAccums[i] = 0;
          } else {
            Promise.all(this.finished).then(() => {
              this.gpuAccums[i] = 0;
              this.finished = [];
            });
          }
        }

        this.paramFrame = this.frameId;
        this.paramTime = t;
      }
    }

    if (!this.detected || !this.chartFrame) {
      this.chartFrame = this.frameId;
      this.chartTime = t;
      this.circularId = 0;
    } else {
      const timespan = t - this.chartTime;
      let hz = this.chartHz * timespan / 1e3;

      while (--hz > 0 && this.detected) {
        const frameCount = this.frameId - this.chartFrame;
        const fps = frameCount / timespan * 1e3;
        this.chart[this.circularId % this.chartLen] = fps;
        const cpuS = this.cpuAccums[1] / duration + 0.1;
        const gpuS = (this.isWebGL2 ? this.gpuAccums[1] * 2 : Math.round(this.gpuAccums[1] / duration * 100)) + 4;

        if (gpuS > 0) {
          this.gpuChart[this.circularId % this.chartLen] = gpuS;
        }

        if (cpuS > 0) {
          this.cpuChart[this.circularId % this.chartLen] = cpuS;
        }

        for (let i = 0; i < this.names.length; i++) {
          this.chartLogger({
            i,
            data: {
              fps: this.chart,
              gpu: this.gpuChart,
              cpu: this.cpuChart
            },
            circularId: this.circularId
          });
        }

        this.circularId++;
        this.chartFrame = this.frameId;
        this.chartTime = t;
      }
    }
  }

  startGpu() {
    const gl = this.gl;
    const ext = this.extension;
    let query;

    if (this.isWebGL2) {
      query = gl.createQuery();

      if (query instanceof WebGLQuery) {
        gl.beginQuery(ext.TIME_ELAPSED_EXT, query);
      }
    } else {
      return;
    }

    if (!query) {
      return;
    }

    this.activeQueries++;

    const checkQuery = () => {
      if (!query || !this.isWebGL2) {
        return;
      }

      let available, disjoint, ns;

      if (this.isWebGL2) {
        available = gl.getQueryParameter(query, gl.QUERY_RESULT_AVAILABLE);
        disjoint = gl.getParameter(ext.GPU_DISJOINT_EXT);
        ns = gl.getQueryParameter(query, gl.QUERY_RESULT);
      } else {
        available = ext.getQueryObjectEXT(query, ext.QUERY_RESULT_AVAILABLE_EXT);
        disjoint = gl.getParameter(ext.GPU_DISJOINT_EXT);
        ns = ext.getQueryObjectEXT(query, ext.QUERY_RESULT_EXT);
      }

      const ms = ns * 1e-6;

      if (available) {
        if (!disjoint) {
          this.activeAccums.forEach((_active, i) => {
            this.gpuAccums[i] = ms;
          });
        }

        this.activeQueries--;
      } else {
        window.requestAnimationFrame(checkQuery);
      }
    };

    window.requestAnimationFrame(checkQuery);
  }

  endGpu() {
    const ext = this.extension;
    const gl = this.gl;

    if (ext === null || !this.isWebGL2) {
      return;
    }

    if (this.isWebGL2 && ext.TIME_ELAPSED_EXT) {
      gl.endQuery(ext.TIME_ELAPSED_EXT);
    } else if (ext.TIME_ELAPSED_EXT) {
      ext.endQueryEXT(ext.TIME_ELAPSED_EXT);
    }
  }

  begin(name) {
    this.startGpu();
    this.updateAccums(name);
  }

  end(name) {
    this.endGpu();
    this.updateAccums(name);
  }

  updateAccums(name) {
    let nameId = this.names.indexOf(name);

    if (nameId === -1) {
      nameId = this.names.length;
      this.addUI(name);
    }

    const t = this.now();
    const dt = t - this.t0;

    for (let i = 0; i < nameId + 1; i++) {
      if (this.activeAccums[i]) {
        this.cpuAccums[i] += dt;
      }
    }

    this.activeAccums[nameId] = !this.activeAccums[nameId];
    this.t0 = t;
  }

}

let PerfLib;
const usePerfStore = create(() => ({
  log: null,
  paused: false,
  chart: {
    data: {
      fps: [],
      gpu: [],
      cpu: []
    },
    circularId: 0
  },
  gl: {
    info: null
  }
}));
const usePerfFunc = () => {
  var _usePerfStore;

  return {
    log: usePerfStore(state => state.log),
    gl: (_usePerfStore = usePerfStore(state => state.gl)) === null || _usePerfStore === void 0 ? void 0 : _usePerfStore.info
  };
};
const Headless = ({
  trackGPU,
  chart
}) => {
  const {
    gl
  } = useThree();
  const mounted = useRef(false);
  usePerfStore.setState({
    gl
  });
  useEffect(() => {
    gl.info.autoReset = false;

    if (!PerfLib && gl.info) {
      PerfLib = new GLPerf({
        trackGPU: trackGPU,
        chartLen: chart ? chart.length : 120,
        chartHz: chart ? chart.hz : 35,
        gl: gl.getContext(),
        chartLogger: chart => {
          usePerfStore.setState({
            chart
          });
        },
        paramLogger: logger => {
          if (PerfLib && gl.info) {
            PerfLib.factorGPU = 1 / gl.info.render.calls;
          }

          usePerfStore.setState({
            log: {
              cpu: logger.cpu,
              gpu: logger.gpu,
              mem: logger.mem,
              fps: logger.fps,
              totalTime: logger.duration,
              frameCount: logger.frameCount
            }
          });
        }
      });
    }

    if (PerfLib && gl.info) {
      PerfLib.gl = gl.getContext();
      const unsub1 = addEffect(() => {
        if (usePerfStore.getState().paused) {
          usePerfStore.setState({
            paused: false
          });
        }

        if (PerfLib && gl.info) {
          gl.info.reset();
          PerfLib.begin('profiler');
        }

        return false;
      });
      const unsub2 = addAfterEffect(() => {
        if (PerfLib) {
          PerfLib.end('profiler');
          PerfLib.nextFrame(window.performance.now());
        }

        return false;
      });
      return () => {
        unsub1();
        unsub2();
      };
    } else {
      return undefined;
    }
  }, [gl, trackGPU, chart]);
  useEffect(() => {
    const unsub = addTail(() => {
      if (PerfLib && mounted.current) {
        PerfLib.paused = true;
        usePerfStore.setState({
          paused: true,
          log: {
            cpu: 0,
            gpu: 0,
            mem: 0,
            fps: 0,
            totalTime: 0,
            frameCount: 0
          }
        });
      }

      mounted.current = true;
      return false;
    });
    return () => {
      unsub();
    };
  }, [mounted]);
  return null;
};

const PerfS = styled('div', {
  position: 'fixed',
  top: 0,
  right: 0,
  zIndex: 9999,
  fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif`,
  backgroundColor: 'rgba(36, 36, 36, 1)',
  color: '#fff',
  pointerEvents: 'none',
  margin: 0,
  padding: '4px 6px',
  '-webkit-font-smoothing': 'antialiased',
  '-moz-osx-font-smoothing': 'grayscale',
  userSelect: 'none',
  '&.top-left': {
    right: 'initial',
    left: 0
  },
  '&.bottom-left': {
    right: 'initial',
    top: 'initial',
    bottom: 0,
    left: 0,
    '.__perf_toggle': {
      top: '-20px',
      bottom: 'initial'
    }
  },
  '&.bottom-right': {
    top: 'initial',
    bottom: 0,
    '.__perf_toggle': {
      top: '-20px',
      bottom: 'initial'
    }
  },
  '*': {
    margin: '0',
    padding: '0',
    border: '0',
    fontSize: '100%',
    lineHeight: '1',
    font: 'inherit',
    verticalAlign: 'baseline'
  }
});
const PerfI = styled('i', {
  display: 'inline-flex',
  fontStyle: 'normal',
  padding: 0,
  lineHeight: '13px',
  fontSize: '14px',
  width: '62px',
  position: 'relative',
  fontWeight: 500,
  letterSpacing: '0px',
  textAlign: 'left',
  height: '29px',
  whiteSpace: 'nowrap',
  justifyContent: 'space-evenly',
  fontVariantNumeric: 'tabular-nums',
  svg: {
    padding: 0,
    color: 'rgba(145, 145, 145, 0.12)',
    fontSize: '22px',
    position: 'absolute',
    zIndex: 1,
    maxHeight: '20px',
    left: ' 50%',
    marginLeft: '-23px',
    top: '4px'
  }
});
const PerfB = styled('span', {
  verticalAlign: 'bottom',
  position: 'absolute',
  bottom: '5px',
  color: 'rgba(101, 197, 188, 1)',
  textAlign: 'revert',
  letterSpacing: '1px',
  fontSize: '8px',
  fontWeight: '500',
  right: 0
});
const Graph = styled('div', {
  height: '61px',
  overflow: 'hidden',
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  width: '310px',
  margin: '0 auto',
  maxWidth: '312px'
});
const Graphpc = styled('div', {
  textAlign: 'center',
  fontWeight: 700,
  fontSize: '12px',
  lineHeight: '12px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  verticalAlign: 'middle',
  color: '#f1f1f1',
  padding: '7px',
  width: '100%',
  backgroundColor: 'rgba(36, 36, 37, 0.8)',
  zIndex: 1,
  position: 'absolute',
  height: '100%'
});
const Toggle = styled('div', {
  pointerEvents: 'auto',
  justifyContent: 'center',
  display: 'block',
  cursor: 'pointer',
  fontSize: '12px',
  backgroundColor: 'rgb(41, 43, 45)',
  marginTop: '6px',
  width: 'auto',
  margin: '0 auto',
  color: 'rgba(145, 145, 145, 1)',
  textAlign: 'center',
  position: 'absolute',
  right: 0,
  bottom: ' -20px',
  padding: '4px 6px',
  svg: {
    width: '12px',
    height: '12px',
    float: 'left'
  }
});

const colorsGraph = colorBlind => {
  const colors = {
    fps: colorBlind ? '100, 143, 255' : '238,38,110',
    cpu: colorBlind ? '254, 254, 98' : '66,226,46',
    gpu: colorBlind ? '254,254,254' : '253,151,31'
  };
  return colors;
};

const ChartCurve = ({
  cg,
  canvas,
  colorBlind,
  trackGPU
}) => {
  const viewport = {
    x: 0,
    y: 0,
    width: cg.canvas.width,
    height: cg.canvas.height
  };
  const coords = cg.coordinate.cartesian(cg.scale.linear([0, 1], [0, viewport.width - 4]), cg.scale.linear([0, 1], [10, viewport.height - 4]));
  const {
    circularId,
    data
  } = usePerfStore(state => state.chart);

  const toPoints = (element, factor = 1) => {
    let maxVal = 0;
    let pointsX = [];
    let pointsY = [];
    const chart = data[element];

    if (!chart || chart.length === 0) {
      return {
        pointsX: [0],
        pointsY: [0]
      };
    }

    let len = chart.length;

    for (let i = 0; i < len; i++) {
      let id = (circularId + i + 1) % len;

      if (chart[id] !== undefined) {
        if (chart[id] > maxVal) {
          maxVal = chart[id] * factor;
        }

        pointsX.push(i / (len - 1));
        pointsY.push(Math.min(100, chart[id]) * factor / 100);
      }
    }

    const graph = {
      pointsX,
      pointsY,
      maxVal,
      element
    };
    return graph;
  };

  useEffect(() => {
    const graphs = [toPoints('fps'), toPoints('cpu')];

    if (trackGPU) {
      graphs.push(toPoints('gpu'));
    }

    const fps = graphs[0];
    const cpu = graphs[1];

    const arrData = [cg.lineStrip(fps.pointsX, fps.pointsY, {
      colors: colorBlind ? [100 / 255, 143 / 255, 255 / 255, 1] : [238 / 255, 38 / 255, 110 / 255, 1],
      widths: 1.5
    }), cg.lineStrip(cpu.pointsX, cpu.pointsY, {
      colors: colorBlind ? [254 / 255, 254 / 255, 98 / 255, 1] : [66 / 255, 226 / 255, 46 / 255, 1],
      widths: 1.5
    })];

    if (trackGPU) {
      const gpu = graphs[2];
      arrData.push(cg.lineStrip(gpu.pointsX, gpu.pointsY, {
        colors: colorBlind ? [254 / 255, 254 / 255, 255 / 255, 1] : [253 / 255, 151 / 255, 31 / 255, 1],
        widths: 1.5
      }));
    }

    cg.clear([0.141, 0.141, 0.141, 1]);
    cg.render(coords, viewport, arrData);
    cg.copyTo(viewport, canvas.current);
  }, [circularId]);
  return null;
};

const ChartUI = ({
  colorBlind,
  trackGPU
}) => {
  const canvas = useRef(undefined);
  const [cg, setcg] = useState(null);
  useEffect(() => {
    if (canvas.current) {
      import('candygraph').then(module => {
        const CandyGraph = module.CandyGraph;
        const cg = new CandyGraph(canvas.current);
        cg.canvas.width = 310;
        cg.canvas.height = 100;
        setcg(cg);
      });
    }
  }, [canvas.current]);
  const paused = usePerfStore(state => state.paused);
  return React__default.createElement(Graph, null, React__default.createElement("canvas", {
    ref: canvas,
    style: {
      width: `${cg ? cg.canvas.width : 0}px`,
      height: `${cg ? cg.canvas.height : 0}px`,
      marginBottom: `-42px`,
      position: 'relative'
    }
  }), !paused && cg && React__default.createElement(ChartCurve, {
    colorBlind: colorBlind,
    trackGPU: trackGPU,
    cg: cg,
    canvas: canvas
  }), paused && React__default.createElement(Graphpc, null, React__default.createElement(GiPauseButton, null), " PAUSED"));
};

const PerfUI = ({
  showGraph,
  trackGPU,
  colorBlind,
  openByDefault
}) => {
  const log = usePerfStore(state => state.log);
  const gl = usePerfStore(state => state.gl);
  return log ? React__default.createElement("div", null, React__default.createElement(PerfI, null, React__default.createElement(RiCpuLine, null), React__default.createElement(PerfB, {
    style: showGraph ? {
      color: `rgb(${colorsGraph(colorBlind).cpu})`
    } : {}
  }, "CPU"), ' ', React__default.createElement("span", null, (Math.round(log.cpu * 100) || 0).toFixed(2), "%")), React__default.createElement(PerfI, null, React__default.createElement(RiCpuFill, null), React__default.createElement(PerfB, {
    style: showGraph && trackGPU ? {
      color: `rgb(${colorsGraph(colorBlind).gpu})`
    } : {}
  }, "GPU"), ' ', React__default.createElement("span", null, (Math.round(log.gpu * 1000) / 1000 || 0).toFixed(2)), React__default.createElement("small", null, "ms")), React__default.createElement(PerfI, null, React__default.createElement(FaMemory, null), React__default.createElement(PerfB, null, "Memory"), " ", log.mem, React__default.createElement("small", null, "mb")), React__default.createElement(PerfI, null, React__default.createElement(VscPulse, null), React__default.createElement(PerfB, {
    style: showGraph ? {
      color: `rgb(${colorsGraph(colorBlind).fps})`
    } : {}
  }, "FPS"), ' ', React__default.createElement("span", null, log.fps)), gl && React__default.createElement(PerfI, null, React__default.createElement(BsTriangle, null), React__default.createElement(PerfB, null, "Triangles"), " ", React__default.createElement("span", null, gl.info.render.triangles)), gl && React__default.createElement(PerfThree, {
    openByDefault: openByDefault
  })) : null;
};

const PerfThree = ({
  openByDefault
}) => {
  const {
    info
  } = usePerfStore(state => state.gl);
  const [show, set] = React__default.useState(openByDefault);
  return React__default.createElement("span", null, info && show && React__default.createElement("div", null, React__default.createElement(PerfI, null, React__default.createElement(AiOutlineCodeSandbox, null), React__default.createElement(PerfB, null, info.memory.geometries === 1 ? 'Geometry' : 'Geometries'), ' ', React__default.createElement("span", null, info.memory.geometries)), React__default.createElement(PerfI, null, React__default.createElement(FaRegImages, null), React__default.createElement(PerfB, null, info.memory.textures === 1 ? 'Texture' : 'Textures'), ' ', React__default.createElement("span", null, info.memory.textures)), React__default.createElement(PerfI, null, React__default.createElement(FiLayers, null), React__default.createElement(PerfB, null, info.render.calls === 1 ? 'call' : 'calls'), ' ', React__default.createElement("span", null, info.render.calls)), React__default.createElement(PerfI, null, React__default.createElement(FaServer, null), React__default.createElement(PerfB, null, info.programs.length === 1 ? 'shader' : 'shaders'), ' ', React__default.createElement("span", null, info.programs.length)), React__default.createElement(PerfI, null, React__default.createElement(VscActivateBreakpoints, null), React__default.createElement(PerfB, null, "Points"), " ", React__default.createElement("span", null, info.render.points))), React__default.createElement(Toggle, {
    className: '__perf_toggle',
    onClick: () => {
      set(!show);
    }
  }, show ? React__default.createElement("span", null, React__default.createElement(RiArrowDownSFill, null), " Minimize") : React__default.createElement("span", null, React__default.createElement(RiArrowRightSFill, null), " More")));
};

const Gui = ({
  showGraph,
  colorBlind,
  trackGPU,
  openByDefault,
  className,
  position,
  chart
}) => {
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(Headless, {
    trackGPU: trackGPU,
    chart: chart
  }), React__default.createElement(Html, {
    transform: false
  }, React__default.createElement(PerfS, {
    className: (className ? ' '.concat(className) : ' ') + ` ${position ? position : ''}`
  }, React__default.createElement(PerfUI, {
    colorBlind: colorBlind,
    showGraph: showGraph,
    trackGPU: trackGPU,
    openByDefault: openByDefault
  }), showGraph && React__default.createElement(ChartUI, {
    colorBlind: colorBlind,
    trackGPU: trackGPU
  }))));
};

const Perf = ({
  headless: _headless = false,
  colorBlind: _colorBlind = false,
  showGraph: _showGraph = true,
  trackGPU: _trackGPU = true,
  openByDefault: _openByDefault = false,
  position: _position = 'top-right',
  chart: _chart = {
    length: 30,
    hz: 10
  },
  className
}) => {
  return _headless ? React__default.createElement(Headless, {
    chart: _chart
  }) : React__default.createElement(Gui, {
    colorBlind: _colorBlind,
    showGraph: _showGraph,
    trackGPU: _trackGPU,
    openByDefault: _openByDefault,
    className: className,
    position: _position,
    chart: _chart
  });
};
const usePerf = usePerfFunc;

export { Perf, usePerf };
